import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { logger } from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHome } from '@fortawesome/free-solid-svg-icons';
import './ClientesPage.css';

interface Cliente {
  id?: string;
  name_cliente?: string;
  email_cliente?: string;
  end_ciente?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  // Campos alternativos que podem existir no banco
  "Nome do cliente"?: string;
  "E-mail do cliente"?: string;
  "Endereço"?: string;
  "Cidade"?: string;
  "Estado"?: string;
  "CEP"?: string;
  [key: string]: any; // Para aceitar qualquer campo adicional
}

const ClientesPage: React.FC = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Buscar clientes do Supabase
  const fetchClientes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar todos os clientes - primeiro tentar com select * para ver todas as colunas
      const { data, error: supabaseError } = await supabase
        .from('cliente')
        .select('*');

      if (supabaseError) {
        throw supabaseError;
      }

      setClientes(data || []);
      logger.info(`Clientes carregados: ${data?.length || 0}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao carregar clientes: ${errorMessage}`);
      logger.error('Erro ao buscar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Carregar clientes na montagem do componente
  useEffect(() => {
    fetchClientes();
  }, []);

  // Funções auxiliares para acessar dados de forma flexível
  const getClienteName = (cliente: Cliente) => {
    return cliente.name_cliente || cliente["Nome do cliente"] || '';
  };

  const getClienteEmail = (cliente: Cliente) => {
    return cliente.email_cliente || cliente["E-mail do cliente"] || '';
  };

  const getClienteEndereco = (cliente: Cliente) => {
    return cliente.end_ciente || cliente["Endereço"] || '';
  };

  const getClienteCidade = (cliente: Cliente) => {
    return cliente.cidade || cliente["Cidade"] || '';
  };

  const getClienteEstado = (cliente: Cliente) => {
    return cliente.estado || cliente["Estado"] || '';
  };

  const getClienteCep = (cliente: Cliente) => {
    return cliente.cep || cliente["CEP"] || '';
  };

  // Filtrar clientes baseado na busca
  const filteredClientes = clientes.filter(cliente => {
    // Tratar valores nulos/undefined antes de chamar toLowerCase
    const name = getClienteName(cliente);
    const email = getClienteEmail(cliente);
    const endereco = getClienteEndereco(cliente);
    const cidade = getClienteCidade(cliente);
    const estado = getClienteEstado(cliente);
    const cep = getClienteCep(cliente);

    const matchesSearch = 
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      endereco.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estado.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cep.includes(searchTerm);

    return matchesSearch;
  });

  // Calcular paginação
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClientes = filteredClientes.slice(startIndex, endIndex);

  // Formatar CEP
  const formatCEP = (cep?: string) => {
    if (!cep) return '-';
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Função para voltar à página inicial
  const handleGoHome = () => {
    navigate('/');
  };

  // Função para voltar à página anterior
  const handleGoBack = () => {
    navigate(-1);
  };

  // Funções de paginação
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="clientes-loading">
        <div className="clientes-loading-spinner">
          <div className="clientes-loading-spinner-icon"></div>
        </div>
        <p className="clientes-loading-text">Carregando clientes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clientes-error">
        <div className="clientes-error-container">
          <h3 className="clientes-error-title">Erro ao carregar clientes</h3>
          <p>{error}</p>
          <button
            onClick={fetchClientes}
            className="clientes-error-btn"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header />
      
      <div className="clientes-container">
        {/* Cabeçalho da página */}
        <div className="clientes-header">
          <div className="clientes-header-top">
            <div className="clientes-header-actions">
              <button 
                onClick={handleGoBack}
                className="clientes-btn-back"
                title="Voltar"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Voltar
              </button>
              <button 
                onClick={handleGoHome}
                className="clientes-btn-home"
                title="Página inicial"
              >
                <FontAwesomeIcon icon={faHome} />
                Início
              </button>
            </div>
          </div>
          
          <div className="clientes-header-content">
            <h1 className="clientes-title">
              Consulta de Clientes
            </h1>
            <p className="clientes-subtitle">
              Visualize todos os clientes cadastrados no sistema
            </p>
          </div>
        </div>

      {/* Filtros */}
      <div className="clientes-filters">
        <div className="clientes-filters-grid">
          {/* Busca */}
          <div className="clientes-search-group">
            <label htmlFor="search" className="clientes-search-label">
              Buscar cliente
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset para primeira página ao buscar
              }}
              placeholder="Nome, email, endereço, cidade, estado ou CEP..."
              className="clientes-search-input"
            />
          </div>

          {/* Informações de resultado */}
          <div className="clientes-results-info">
            <div className="clientes-results-text">
              {filteredClientes.length} cliente(s) encontrado(s)
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de clientes - Desktop */}
      <div className="clientes-table-container">
        <div className="clientes-table-wrapper">
          <table className="clientes-table">
            <thead className="clientes-table-header">
              <tr>
                <th className="clientes-table-header-row">
                  Nome do Cliente
                </th>
                <th className="clientes-table-header-row">
                  E-mail do Cliente
                </th>
                <th className="clientes-table-header-row">
                  Endereço
                </th>
                <th className="clientes-table-header-row">
                  Cidade
                </th>
                <th className="clientes-table-header-row">
                  Estado
                </th>
                <th className="clientes-table-header-row">
                  CEP
                </th>
              </tr>
            </thead>
            <tbody className="clientes-table-body">
              {currentClientes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="clientes-table-cell-center">
                    {searchTerm 
                      ? 'Nenhum cliente encontrado com os filtros aplicados'
                      : 'Nenhum cliente cadastrado'
                    }
                  </td>
                </tr>
              ) : (
                currentClientes.map((cliente, index) => (
                  <tr key={cliente.id || index} className="clientes-table-row">
                    <td className="clientes-table-cell">
                      <div className="clientes-client-name">
                        {getClienteName(cliente) || '-'}
                      </div>
                    </td>
                    <td className="clientes-table-cell">
                      <div className="clientes-client-email">
                        {getClienteEmail(cliente) || '-'}
                      </div>
                    </td>
                    <td className="clientes-table-cell">
                      <div className="clientes-client-address">
                        {getClienteEndereco(cliente) || '-'}
                      </div>
                    </td>
                    <td className="clientes-table-cell">
                      <div className="clientes-client-city">
                        {getClienteCidade(cliente) || '-'}
                      </div>
                    </td>
                    <td className="clientes-table-cell">
                      <div className="clientes-client-state">
                        {getClienteEstado(cliente) || '-'}
                      </div>
                    </td>
                    <td className="clientes-table-cell">
                      <div className="clientes-client-cep">
                        {formatCEP(getClienteCep(cliente))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cards para Mobile */}
      <div className="clientes-mobile-cards">
        {currentClientes.length === 0 ? (
          <div className="clientes-mobile-card">
            <div className="clientes-mobile-card-header">
              <div className="clientes-mobile-card-name">
                {searchTerm 
                  ? 'Nenhum cliente encontrado com os filtros aplicados'
                  : 'Nenhum cliente cadastrado'
                }
              </div>
            </div>
          </div>
        ) : (
          currentClientes.map((cliente, index) => (
            <div key={cliente.id || index} className="clientes-mobile-card">
              <div className="clientes-mobile-card-header">
                <div className="clientes-mobile-card-name">
                  {getClienteName(cliente) || '-'}
                </div>
                <div className="clientes-mobile-card-email">
                  {getClienteEmail(cliente) || '-'}
                </div>
              </div>
              <div className="clientes-mobile-card-body">
                <div className="clientes-mobile-card-field">
                  <div className="clientes-mobile-card-label">Endereço</div>
                  <div className="clientes-mobile-card-value">
                    {getClienteEndereco(cliente) || '-'}
                  </div>
                </div>
                <div className="clientes-mobile-card-field">
                  <div className="clientes-mobile-card-label">Cidade</div>
                  <div className="clientes-mobile-card-value">
                    {getClienteCidade(cliente) || '-'}
                  </div>
                </div>
                <div className="clientes-mobile-card-field">
                  <div className="clientes-mobile-card-label">Estado</div>
                  <div className="clientes-mobile-card-value">
                    {getClienteEstado(cliente) || '-'}
                  </div>
                </div>
                <div className="clientes-mobile-card-field">
                  <div className="clientes-mobile-card-label">CEP</div>
                  <div className="clientes-mobile-card-value">
                    {formatCEP(getClienteCep(cliente))}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="clientes-pagination">
          <div className="clientes-pagination-mobile">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="clientes-pagination-btn"
            >
              Anterior
            </button>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="clientes-pagination-btn-ml"
            >
              Próximo
            </button>
          </div>
          <div className="clientes-pagination-desktop">
            <div>
              <p className="clientes-pagination-info">
                Mostrando <span className="clientes-pagination-info-bold">{startIndex + 1}</span> até{' '}
                <span className="clientes-pagination-info-bold">{Math.min(endIndex, filteredClientes.length)}</span> de{' '}
                <span className="clientes-pagination-info-bold">{filteredClientes.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="clientes-pagination-nav" aria-label="Pagination">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="clientes-pagination-btn-nav"
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Números das páginas */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`clientes-pagination-page-btn ${
                          page === currentPage
                            ? 'clientes-pagination-page-btn-active'
                            : 'clientes-pagination-page-btn-inactive'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return (
                      <span key={page} className="clientes-pagination-ellipsis">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="clientes-pagination-btn-nav-right"
                >
                  <span className="sr-only">Próximo</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Rodapé com informações */}
      <div className="clientes-footer">
        <p>
          Total de {clientes.length} clientes cadastrados
          {searchTerm && ` (filtrado por: "${searchTerm}")`}
        </p>
        <p className="clientes-footer-text">
          Última atualização: {new Date().toLocaleString('pt-BR')}
        </p>
      </div>
      </div>
      
      <Footer />
    </div>
  );
};

export { ClientesPage };